import { CSSProperties, StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../web/components-v2/Theme/theme';
import { eventTintColor, navigation, titles } from '../../../../../web/styles/colors';
import { CssSelectors, absoluteCenterStyle, baseStyles } from '../../../../../web/styles/styles';
const { colors, common, space, spacing } = THEME;

export const styleSheet = StyleSheet.create({
	actionEntities: {
		alignItems: 'center',
		color: colors.primary.main,
		cursor: 'pointer',
		display: 'inline-flex',
		fontSize: 14,
		marginLeft: 30,
	},
	addCustomCard: {
		color: colors.hover.main,
	},
	addLogoButtonLabel: {
		height: 50,
	},
	buttonsWrapper: {
		display: 'flex',
	},
	column: {
		marginBottom: 15,
		marginTop: spacing.xs,
		width: '50%',
	},
	columnButtonIcon: {
		minWidth: 16,
	},
	companyLogoButtonWrap: {
		':hover': {
			...CssSelectors.allChildren(
				{
					display: 'block',
				},
				'img'
			),
			...CssSelectors.allChildren(
				{
					display: 'block',
				},
				'.remove-logo-button'
			),
		},
		...CssSelectors.allChildren(
			{
				display: 'none',
			},
			'img'
		),
		...CssSelectors.allChildren(
			{
				display: 'none',
			},
			'.remove-logo-button'
		),
		display: 'flex',
		height: 50,
		minWidth: 400,
		position: 'relative',
	},
	container: {
		display: 'flex',
		justifyContent: 'flex-start',
		marginTop: 20,
		minHeight: 400,
		width: '100%',
	},
	customTextColor: {
		color: 'rgb(4, 108, 182)',
	},
	customEditButton: {
		left: 0,
		position: 'absolute',
	},
	customLabelWrap: {
		display: 'inline-flex',
		paddingLeft: 30,
		position: 'relative',
	},
	customRow: {
		...(baseStyles.brandLink as CSSProperties),
		fontSize: 13,
	},
	deleteKeyFactsHeader: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		margin: '0 20px',
	},
	deleteRenewKeyFactsModal: {
		maxWidth: 400,
	},
	disable: {
		opacity: 0.6,
	},
	dropdown: {
		minWidth: 130,
	},
	expandable: {
		overflow: 'hidden',
		position: 'relative',
	},
	exportButton: {
		...(baseStyles.brandLink as CSSProperties),
		fontSize: 13,
	},
	hawksoftInfo: {
		color: navigation,
		fontSize: 13,
		marginTop: 6,
	},
	infoFieldContainerBottom: {
		alignItems: 'center',
		borderBottom: 'solid 1px rgb(211, 219, 222)',
		display: 'flex',
		height: 55,
		justifyContent: 'space-between',
		marginBottom: 0,
		paddingLeft: 15,
		paddingRight: 10,
	},
	infoFieldContainerFullContainer: {
		borderLeft: 'solid 1px rgb(211, 219, 222)',
		borderRight: 'solid 1px rgb(211, 219, 222)',
	},
	infoFieldValueBottom: {
		alignItems: 'center',
		color: titles,
		display: 'flex',
		fontSize: 13,
		position: 'relative',
	},
	infoFieldBottomNewIcon: {
		position: 'absolute',
		top: 0,
		left: 0,
		backgroundColor: eventTintColor,
		borderRadius: common.borderRadius,
		fontSize: 11,
		color: titles,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '2px 4px',
		gap: spacing.xs,

		...CssSelectors.allChildren(
			{
				width: 10,
				height: 10,
			},
			'svg'
		),
	},
	loading: {
		...absoluteCenterStyle,
	},
	logoImage: {
		height: 50,
		left: 300,
		position: 'absolute',
		top: '50%',
		transform: 'translateY(-50%)',
	},
	multiSelectPlaceholder: {
		color: '#999',
		fontSize: 13,
	},
	/**
	 * The dropdown for a Multi row, which sits beside a toggle and so needs a gap the plain dropdown
	 * does not. It has to arrive as one style object: SelectTrigger builds its className from
	 * `styles`, and a `className` prop would replace that string wholesale rather than adding to it,
	 * taking the trigger's own border and flex layout with it.
	 */
	multiDropdown: {
		marginRight: 10,
		minWidth: 130,
	},
	numericInput: {
		width: 110,
	},
	resetAccount: {
		...(baseStyles.brandLink as CSSProperties),
		color: colors.danger.main,
		fontSize: 13,
	},
	settingsSearch: {
		marginRight: 10,
		minWidth: 400,
	},
	settingsSearchContainer: {
		display: 'flex',
	},
	// ---- Detail variant: sizes to content, help text behind the row's hover icon ----
	detailRow: {
		width: '100%',
	},
	/** Suppresses a row's own divider, for rows whose separation is drawn by a wrapper instead. */
	noDivider: {
		borderBottom: 'none',
	},
	/**
	 * AccountDetailsInfoField gives its label a fixed 17px height plus truncateText (overflow
	 * hidden, nowrap), which slices a label too long for one line in a narrower column. This
	 * releases all three.
	 *
	 * The direction stays the base row: the label's only siblings are the dependent badge and the
	 * help icon, both of which belong beside the name rather than under it.
	 *
	 * `flexBasis: auto` rather than `flex: 1`: a zero basis let the row's control — a Select trigger
	 * is nowrap and fit-content, so it cannot shrink — squeeze the label to a couple of characters,
	 * and with overflow released above the squeezed text painted straight over that control instead
	 * of clipping. Sizing the label to its own content instead makes the pair overflow the line,
	 * which `detailRowHeight` below wraps.
	 */
	detailLabelContainer: {
		alignItems: 'center',
		flexBasis: 'auto',
		flexGrow: 1,
		flexShrink: 1,
		flexWrap: 'wrap',
		height: 'auto',
		// A floor to wrap against; below it a label is unreadable whatever the control does.
		minWidth: 120,
		overflow: 'visible',
		// Last resort for a single word longer than the column, which no wrap can place.
		overflowWrap: 'break-word',
		textOverflow: 'clip',
		whiteSpace: 'normal',
	},
	/**
	 * Keeps the control whole. It is the item that cannot shrink, so it is the one that moves: when
	 * the label and the control no longer fit on one line the control drops below the label, staying
	 * right-aligned in both states.
	 */
	detailValueContainer: {
		flexShrink: 0,
		marginLeft: 'auto',
		maxWidth: '100%',
	},
	detailRowHeight: {
		height: 'auto',
		// Shorter than the 55px the tab used, which the help text needed. Behind the icon it no
		// longer claims a second line, so two columns of rows fit where one used to.
		minHeight: 40,
		paddingBottom: spacing.xs,
		paddingTop: spacing.xs,
		// The row is only as wide as its container, which the admin right pane can halve without the
		// viewport changing. Wrapping is what a too-narrow row does instead of overlapping itself.
		columnGap: `${spacing.sm}px`,
		flexWrap: 'wrap',
		rowGap: spacing.xs,
	},
	detailLabelRow: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: spacing.sm,
	},
	/** A label carrying its own value beneath it, rather than in a sub-row the hub reads as a dependent. */
	settingWithValue: {
		display: 'flex',
		flexDirection: 'column',
		gap: space[8],
	},
	/**
	 * Opts out of `detailValueContainer` dropping the control below a label it no longer fits beside:
	 * these pills make the label a block every time, so the control sank under the last pill.
	 */
	settingWithValueRow: {
		alignItems: 'flex-start',
		flexWrap: 'nowrap',
		// Every other row takes its breathing room from minHeight centring a one-line label, which a
		// block this tall never reaches — without this it falls back to the 4px padding and crowds
		// both dividers. 12 is the grid step nearest the ~11.5px centring leaves.
		paddingBottom: space[12],
		paddingTop: space[12],
	},
	/**
	 * Pills, as the count badge already does: as prose these read as a second title, and the palette's
	 * only lighter grey is 2.9:1 on white, under the floor for content this size.
	 */
	settingValuePills: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: space[8],
	},
	settingValuePill: {
		backgroundColor: colors.background.dark,
		borderRadius: common.borderRadius,
		color: colors.text.main,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.semibold,
		paddingBottom: space[4],
		paddingLeft: space[8],
		paddingRight: space[8],
		paddingTop: space[4],
	},
	/** No pill for an absent value — an empty state is a note, not data. */
	settingValueEmpty: {
		color: colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	subRow: {
		paddingLeft: 30,
	},
	// Puts the control on its own full-width line under the label (rows whose input needs more room).
	stackedRow: {
		alignItems: 'stretch',
		flexDirection: 'column',
		gap: spacing.sm,
		height: 'auto',
		justifyContent: 'center',
		paddingBottom: spacing.md,
		paddingTop: spacing.md,
	},
	emailInput: {
		width: 360,
	},
	savingText: {
		color: navigation,
		fontSize: 12,
		marginLeft: spacing.sm,
	},
	tagSettingsModal: {
		minWidth: 400,
		padding: 20,
	},
	tagSettingsModalTitle: {
		fontSize: 18,
		fontWeight: 600,
		marginBottom: 20,
	},
	tagSettingsModalRow: {
		alignItems: 'center',
		borderBottom: `1px solid ${colors.border.light}`,
		display: 'flex',
		justifyContent: 'space-between',
		padding: '12px 0',
	},
	tagSettingsModalLabel: {
		alignItems: 'center',
		display: 'flex',
		gap: 6,
	},
	tagSettingsModalInfoIcon: {
		cursor: 'pointer',
		display: 'inline-flex',
	},
	tagSettingsModalHoverContent: {
		fontSize: 12,
	},
	tagSettingsModalFooter: {
		display: 'flex',
		gap: 10,
		justifyContent: 'flex-end',
		marginTop: 20,
	},
	tagSettingsWarning: {
		color: colors.danger.main,
		fontSize: 13,
		margin: '4px 0 0',
	},
	tagSettingsInfo: {
		color: colors.text.light,
		fontSize: 13,
		marginTop: 12,
	},
});
